import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import { UsaWrestlingMembership } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';
import { observableInSeparateThread } from '../helper/observable-helper';

/**
 * Service to get usa wrestling membership
 */
@Injectable()
export class UsaWrestlingService {
    private cachedMap: { [apiRequest: string]: UsaWrestlingMembership; } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get usa wrestling athlete membership
     */
    public getAthleteMembership(eventId: string, usawIdOrCardNum: number, divisionId?: string)
    : Observable<UsaWrestlingMembership> {
        const apiRequestUrl: string = this.getApiUrl(eventId, usawIdOrCardNum, divisionId);

        if (this.cachedMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedMap[apiRequestUrl]);
        }
        // Make the http request
        return this.http.get(apiRequestUrl, false, false)
            .pipe(
                map((response: IJsonApiResponse) => (new UsaWrestlingMembership().deserialize(response))),
                catchError((error: HttpErrorResponse) => this.http.handleError(error, null)),
            );
    }

    /**
     * Get usa wrestling coach membership
     */
    public getCoachMembership(eventId: string, usawIdOrCardNum: number, certificationLevel?: string)
    : Observable<UsaWrestlingMembership> {
        const baseUrl: string = this.http.configService.getApiBaseUrl();
        let apiRequestUrl: string = `${baseUrl}/events/${eventId}/wrestlers/usaw-coach-membership/${usawIdOrCardNum}`;

        if (certificationLevel) {
            apiRequestUrl += `/${certificationLevel}`;
        }

        if (this.cachedMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedMap[apiRequestUrl]);
        }
        // Make the http request
        return this.http.get(apiRequestUrl, false, false)
            .pipe(
                map((response: IJsonApiResponse) => (new UsaWrestlingMembership().deserialize(response))),
                catchError((error: HttpErrorResponse) => this.http.handleError(error, null)),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(eventId: string, usawIdOrCardNum: number, divisionId?: string): string {
        const baseUrl: string = this.http.configService.getApiBaseUrl();
        let url: string = `${baseUrl}/events/${eventId}/wrestlers/usaw-athlete-membership/${usawIdOrCardNum}`;

        if (divisionId) {
            url += `/${divisionId}`;
        }

        return url;
    }
}
